import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  UserGroupIcon,
  UserIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  UserMinusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  TrophyIcon,
  StarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import MemberAvatar from '../components/MemberAvatar';
import AttributeLabel from '../components/AttributeLabel';


interface Member {
  id: number;
  vorname: string;
  name: string;
  email: string;
  foto: string | null;
  fotoUpdated: string | null;
  eintritt: string | null;
  membershipYears: number;
  roles: string[];
  active: boolean;
}

interface Structure {
  board: Member[];
  members: Member[];
  guests: Member[];
  trainers: Member[];
  auditors: Member[];
  deactivated: Member[];
}

interface Stats {
  total: number;
  board: number;
  members: number;
  guests: number;
  trainers: number;
  auditors: number;
  deactivated: number;
}

const AdminStructure: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState<Structure | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [activeTab, setActiveTab] = useState<'structure' | 'anniversary'>('structure');
  const [profileImages, setProfileImages] = useState<Record<number, string>>({});
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    board: true,
    trainers: true,
    auditors: true,
    members: true,
    guests: false,
    deactivated: false,
  });

  const isBoardMember = user?.roles?.some((r: any) => r.name === 'board');
  const BASE_URL = localStorage.getItem('club_url') || 'http://localhost:4000';

  useEffect(() => {
    if (isBoardMember) {
      fetchStructure();
    }
  }, [isBoardMember]);

  const fetchProfileImage = (memberId: number, fotoUpdated?: string | null) => {
    const token = localStorage.getItem('jwt_token');
    if (!token) return;

    const timestamp = fotoUpdated || new Date().getTime();
    const imageUrl = `${BASE_URL}/api/members/user-image-url/${memberId}?size=S&token=${token}&t=${timestamp}`;
    setProfileImages(prev => ({ ...prev, [memberId]: imageUrl }));
  };

  const fetchStructure = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/member-structure');
      if (response.data.success) {
        setStructure(response.data.structure);
        setStats(response.data.stats);

        // Load profile images for all members with photos
        const allMembers = [
          ...response.data.structure.board,
          ...response.data.structure.members,
          ...response.data.structure.trainers,
          ...response.data.structure.auditors,
          ...response.data.structure.guests,
          ...response.data.structure.deactivated,
        ];

        allMembers.forEach((member: Member) => {
          if (member.foto) {
            fetchProfileImage(member.id, member.fotoUpdated);
          }
        });
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get all active members sorted by membership years for anniversary tab
  const allActiveMembers = useMemo(() => {
    if (!structure) return [];
    const allMembers = [
      ...structure.board,
      ...structure.members,
      ...structure.trainers,
      ...structure.auditors,
    ];
    // Remove duplicates (members can be in multiple categories)
    const uniqueMembers = allMembers.filter((member, index, self) =>
      index === self.findIndex((m) => m.id === member.id)
    );
    // Sort by membership years descending
    return uniqueMembers.sort((a, b) => b.membershipYears - a.membershipYears);
  }, [structure]);

  // Calculate anniversary statistics
  const anniversaryStats = useMemo(() => {
    if (allActiveMembers.length === 0) return null;

    const years = allActiveMembers.map(m => m.membershipYears);
    const totalYears = years.reduce((sum, y) => sum + y, 0);
    const avgYears = totalYears / years.length;
    const maxYears = Math.max(...years);
    const minYears = Math.min(...years);

    // Group by milestone years
    const milestones = {
      '25+': allActiveMembers.filter(m => m.membershipYears >= 25).length,
      '20-24': allActiveMembers.filter(m => m.membershipYears >= 20 && m.membershipYears < 25).length,
      '15-19': allActiveMembers.filter(m => m.membershipYears >= 15 && m.membershipYears < 20).length,
      '10-14': allActiveMembers.filter(m => m.membershipYears >= 10 && m.membershipYears < 15).length,
      '5-9': allActiveMembers.filter(m => m.membershipYears >= 5 && m.membershipYears < 10).length,
      '1-4': allActiveMembers.filter(m => m.membershipYears >= 1 && m.membershipYears < 5).length,
      '<1': allActiveMembers.filter(m => m.membershipYears < 1).length,
    };

    // Upcoming anniversaries (5, 10, 15, 20, 25, 30 years)
    const currentYear = new Date().getFullYear();
    const upcomingAnniversaries = allActiveMembers.filter(m => {
      const nextMilestone = [5, 10, 15, 20, 25, 30, 35, 40].find(y => y > m.membershipYears);
      if (!nextMilestone) return false;
      const yearsUntil = nextMilestone - m.membershipYears;
      return yearsUntil === 1; // Anniversary next year
    });

    return {
      totalYears,
      avgYears: Math.round(avgYears * 10) / 10,
      maxYears,
      minYears,
      milestones,
      upcomingAnniversaries,
    };
  }, [allActiveMembers]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'board': return <ShieldCheckIcon className="h-5 w-5" />;
      case 'trainers': return <AcademicCapIcon className="h-5 w-5" />;
      case 'auditors': return <ShieldCheckIcon className="h-5 w-5" />;
      case 'members': return <UserGroupIcon className="h-5 w-5" />;
      case 'guests': return <UserIcon className="h-5 w-5" />;
      case 'deactivated': return <UserMinusIcon className="h-5 w-5" />;
      default: return <UserIcon className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'board': return 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-primary-200 dark:border-primary-800';
      case 'trainers': return 'bg-info-100 dark:bg-info-900/30 text-info-600 dark:text-info-400 border-info-200 dark:border-info-800';
      case 'auditors': return 'bg-warning-100 dark:bg-warning-900/30 text-warning-600 dark:text-warning-400 border-warning-200 dark:border-warning-800';
      case 'members': return 'bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400 border-success-200 dark:border-success-800';
      case 'guests': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600';
      case 'deactivated': return 'bg-error-100 dark:bg-error-900/30 text-error-600 dark:text-error-400 border-error-200 dark:border-error-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'board': return 'Vorstand';
      case 'trainers': return 'Trainer';
      case 'auditors': return 'Kassenprüfer';
      case 'members': return 'Mitglieder';
      case 'guests': return 'Gäste';
      case 'deactivated': return 'Deaktiviert';
      default: return category;
    }
  };

  const getMedalColor = (rank: number) => {
    if (rank === 1) return 'text-warning-500';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-amber-600';
    return 'text-gray-300';
  };

  const getMilestoneColor = (years: number) => {
    if (years >= 25) return 'bg-warning-100 dark:bg-warning-900/30 border-warning-300 dark:border-warning-700';
    if (years >= 20) return 'bg-primary-100 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700';
    if (years >= 15) return 'bg-info-100 dark:bg-info-900/30 border-info-300 dark:border-info-700';
    if (years >= 10) return 'bg-success-100 dark:bg-success-900/30 border-success-300 dark:border-success-700';
    if (years >= 5) return 'bg-teal-100 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700';
    return 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
  };

  if (!isBoardMember) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md text-center">
            <ShieldCheckIcon className="h-16 w-16 text-error-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Zugriff verweigert</h2>
            <p className="text-gray-600 dark:text-gray-400">Diese Seite ist nur für Vorstandsmitglieder zugänglich.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderMemberCard = (member: Member, showRank?: number) => {
    return (
      <div
        key={member.id}
        className={`flex items-center gap-3 p-3 rounded-lg border hover:shadow-md transition-shadow ${getMilestoneColor(member.membershipYears)}`}
      >
        {/* Rank */}
        {showRank && showRank <= 3 && (
          <div className="flex-shrink-0">
            <TrophyIcon className={`h-6 w-6 ${getMedalColor(showRank)}`} />
          </div>
        )}
        {showRank && showRank > 3 && (
          <div className="flex-shrink-0 w-6 text-center">
            <span className="text-sm font-medium text-gray-500">{showRank}</span>
          </div>
        )}

        {/* Foto */}
        <MemberAvatar
          member={{
            vorname: member.vorname,
            name: member.name,
            active: member.active ? 1 : 0,
            // roles ist hier nur ein string[] (Namen); Priorität nach bekannter Wichtigkeit
            // ableiten, damit die primäre Rolle (und damit Badge/Farbe) korrekt gewählt wird.
            roles: member.roles?.map(r => ({
              name: r,
              priority: { board: 5, trainer: 4, auditor: 3, member: 2, guest: 1 }[r] ?? 0,
            })),
          }}
          photoUrl={member.foto ? profileImages[member.id] : undefined}
          hasImageError={imageErrors[member.id]}
          onImageError={() => {
            setImageErrors(prev => ({ ...prev, [member.id]: true }));
          }}
          size="md"
        />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate">
            {member.vorname} {member.name}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <AttributeLabel iconKey="memberSince" size="sm" textClassName="text-sm text-gray-500 dark:text-gray-400">Mitglied seit</AttributeLabel> {formatDate(member.eintritt)}
          </p>
        </div>

        {/* Jahre */}
        <div className="flex-shrink-0 text-right">
          <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
            {member.membershipYears}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {member.membershipYears === 1 ? 'Jahr' : 'Jahre'}
          </p>
        </div>
      </div>
    );
  };

  const renderCategory = (category: string, members: Member[]) => {
    const isExpanded = expandedSections[category];
    const count = members.length;

    return (
      <div key={category} className="mb-4">
        {/* Category Header */}
        <button
          onClick={() => toggleSection(category)}
          className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-colors ${getCategoryColor(category)}`}
        >
          {/* Expand Icon */}
          <div className="flex-shrink-0">
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
          </div>

          {/* Category Icon */}
          <div className="flex-shrink-0">
            {getCategoryIcon(category)}
          </div>

          {/* Label */}
          <span className="font-semibold flex-1 text-left">
            {getCategoryLabel(category)}
          </span>

          {/* Count Badge */}
          <span className="px-3 py-1 rounded-full bg-white/50 dark:bg-black/20 font-bold">
            {count}
          </span>
        </button>

        {/* Members List */}
        {isExpanded && members.length > 0 && (
          <div className="mt-2 ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-2">
            {members.map(m => renderMemberCard(m))}
          </div>
        )}

        {isExpanded && members.length === 0 && (
          <div className="mt-2 ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
              Keine Mitglieder in dieser Kategorie
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
          <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <BuildingOfficeIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              <div>
                <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Vereinsstruktur</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Mitgliederbereich</p>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('structure')}
              className={`flex items-center gap-2 px-4 py-3 text-base font-medium border-b-2 transition-colors ${
                activeTab === 'structure'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <BuildingOfficeIcon className="h-5 w-5" />
              Struktur
            </button>
            <button
              onClick={() => setActiveTab('anniversary')}
              className={`flex items-center gap-2 px-4 py-3 text-base font-medium border-b-2 transition-colors ${
                activeTab === 'anniversary'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <TrophyIcon className="h-5 w-5" />
              Jubiläum
            </button>
          </nav>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
          </div>
        )}

        {/* Structure Tab */}
        {!loading && activeTab === 'structure' && structure && (
          <>
            {/* Stats Overview */}
            {stats && (
              <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                  <div>
                    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Aktive Mitglieder</p>
                    <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tabular-nums leading-tight">
                      {stats.total}
                    </p>
                    {stats.deactivated > 0 && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {stats.deactivated} deaktiviert
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800">
                    <span className="tabular-nums font-bold">{stats.board}</span> Vorstand
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300 border border-info-200 dark:border-info-800">
                    <span className="tabular-nums font-bold">{stats.trainers}</span> Trainer
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300 border border-warning-200 dark:border-warning-800">
                    <span className="tabular-nums font-bold">{stats.auditors}</span> Kassenprüfer
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 border border-success-200 dark:border-success-800">
                    <span className="tabular-nums font-bold">{stats.members}</span> Mitglieder
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                    <span className="tabular-nums font-bold">{stats.guests}</span> Gäste
                  </span>
                </div>
              </div>
            )}

            {/* Tree Structure */}
            <div className="space-y-2">
              {renderCategory('board', structure.board)}
              {renderCategory('trainers', structure.trainers)}
              {renderCategory('auditors', structure.auditors)}
              {renderCategory('members', structure.members)}
              {renderCategory('guests', structure.guests)}
              {renderCategory('deactivated', structure.deactivated)}
            </div>
          </>
        )}

        {/* Anniversary Tab */}
        {!loading && activeTab === 'anniversary' && anniversaryStats && (
          <>
            {/* Anniversary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 text-center">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{anniversaryStats.totalYears}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Jahre gesamt</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 text-center">
                <p className="text-3xl font-bold text-info-600 dark:text-info-400">{anniversaryStats.avgYears}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Ø Jahre</p>
              </div>
              <div className="bg-warning-50 dark:bg-warning-900/20 rounded-xl border border-warning-200 dark:border-warning-800 p-5 text-center">
                <p className="text-3xl font-bold text-warning-600 dark:text-warning-400">{anniversaryStats.maxYears}</p>
                <p className="text-sm text-warning-600 dark:text-warning-400">Längstes</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 text-center">
                <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{allActiveMembers.length}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Mitglieder</p>
              </div>
            </div>

            {/* Milestone Distribution */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <StarIcon className="h-5 w-5 text-warning-500" />
                Mitgliedschaftsdauer Verteilung
              </h3>
              <div className="space-y-3">
                {Object.entries(anniversaryStats.milestones).map(([range, count]) => {
                  const percentage = allActiveMembers.length > 0 ? (count / allActiveMembers.length) * 100 : 0;
                  const colors: Record<string, string> = {
                    '25+': 'bg-warning-500',
                    '20-24': 'bg-primary-500',
                    '15-19': 'bg-info-500',
                    '10-14': 'bg-success-500',
                    '5-9': 'bg-teal-500',
                    '1-4': 'bg-gray-400',
                    '<1': 'bg-gray-300',
                  };
                  return (
                    <div key={range} className="flex items-center gap-3">
                      <span className="w-16 text-sm font-medium text-gray-700 dark:text-gray-300">{range} J.</span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${colors[range]} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="w-12 text-sm font-bold text-gray-900 dark:text-white text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming Anniversaries */}
            {anniversaryStats.upcomingAnniversaries.length > 0 && (
              <div className="bg-warning-50 dark:bg-warning-900/20 rounded-xl border border-warning-200 dark:border-warning-800 p-5 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-warning-500" />
                  Jubiläen im nächsten Jahr
                </h3>
                <div className="space-y-2">
                  {anniversaryStats.upcomingAnniversaries.map(member => {
                    const nextMilestone = [5, 10, 15, 20, 25, 30, 35, 40].find(y => y > member.membershipYears);
                    return (
                      <div key={member.id} className="flex items-center gap-3 p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                        <TrophyIcon className="h-5 w-5 text-warning-500" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {member.vorname} {member.name}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">→</span>
                        <span className="font-bold text-warning-600 dark:text-warning-400">
                          {nextMilestone} Jahre
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* All Members Ranked */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <TrophyIcon className="h-5 w-5 text-warning-500" />
                  Rangliste nach Mitgliedschaft
                </h3>
              </div>
              <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto">
                {allActiveMembers.map((member, index) => renderMemberCard(member, index + 1))}
              </div>
            </div>
          </>
        )}
      </div>
  );
};

export default AdminStructure;
